import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import { applyReflowStateAction, updateHiddenElementsAction } from 'editor/src/store/design/slice';
import { Spread } from 'editor/src/store/design/types';
import {
  getOrphanSlotLayoutIds,
  getSlotFrameHiddenUuids,
} from 'editor/src/store/design/util/slotLayout/slotFrameVisibility';
import removeAllSelectedMediaElementsOperation from 'editor/src/store/editor/operation/removeAllSelectedMediaElementsOperation';
import getCurrentSpreadIndex from 'editor/src/store/editor/selector/getCurrentSpreadIndex';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import { captureMessage } from 'editor/src/logger';

import computeReflowState from './computeReflowState';
import getHiddenMediaElementUuids from './getHiddenMediaElementUuids';

const reportedLayoutIds = new Set<string>();

// A design that lost a layout but kept its tagged layers shows every frame at once; reported
// once per layout, from here rather than from the pure lookups the selectors share.
function reportOrphanSlotLayouts(spread: Pick<Spread, 'slotLayouts' | 'pages'>) {
  getOrphanSlotLayoutIds(spread).forEach((layoutId) => {
    if (!reportedLayoutIds.has(layoutId)) {
      reportedLayoutIds.add(layoutId);
      captureMessage('Slot frame layer without its layout', { extra: { layoutId } });
    }
  });
}

/**
 * Sole entry point for recomputing derived spread state. Three producers feed
 * `element.hidden`: Conditions, slot frames and Reflow rows. Reflow additionally mutates
 * `element.y` (collapsed rows stay at authored position; subsequent rows
 * shift up to overlap them) and records the applied shift on each row.
 *
 * The hide channel and the position channel are written in a single batch so
 * render sees a consistent state. NEVER dispatch `updateHiddenElementsAction`
 * or `applyReflowStateAction` directly from anywhere else — that would
 * silently un-hide every other producer's elements OR leave positions out of
 * sync with collapse state.
 */
const updateHiddenElementsOperation =
  (targetSpreadIndex?: number) => (dispatch: ThunkDispatch, getState: () => RootState) => {
    const state = getState();
    const spreadIndex = targetSpreadIndex ?? getCurrentSpreadIndex(state);
    const spread = state.design.designData?.spreads[spreadIndex];
    if (!spread) {
      return;
    }

    reportOrphanSlotLayouts(spread);
    const hasConditions = !!spread.conditionGroup;
    const hasSlotLayouts = !!spread.slotLayouts?.length;
    const hasReflowGroups = !!spread.reflowGroups?.length;
    if (!hasConditions && !hasReflowGroups && !hasSlotLayouts) {
      return;
    }

    const conditionHidden = hasConditions
      ? getHiddenMediaElementUuids(spread.conditionGroup!, spread)
      : getSlotFrameHiddenUuids(spread);
    const reflow = computeReflowState(spread, conditionHidden);
    const hiddenElementIds = new Set<number>([...conditionHidden, ...reflow.hiddenUuids]);

    batch(() => {
      dispatch(
        updateHiddenElementsAction({
          spreadIndex,
          hiddenElementIds: [...hiddenElementIds],
        }),
      );
      if (reflow.elementUpdates.length || reflow.rowShiftUpdates.length) {
        dispatch(
          applyReflowStateAction({
            spreadIndex,
            elementUpdates: reflow.elementUpdates,
            rowShiftUpdates: reflow.rowShiftUpdates,
          }),
        );
      }
    });

    if (state.editor.selectedElementUuids.some((uuid) => hiddenElementIds.has(uuid))) {
      dispatch(removeAllSelectedMediaElementsOperation());
    }
  };

export default updateHiddenElementsOperation;
